import type { BackgroundColor } from '@snapchat/snap-design-system-marketing';
import { createContext } from 'react';

export enum HeaderView {
  FULL_HEADER = 'Full Header',
  /**
   * Minimized header hides all the navigation items, keeping just the snap logo and the call to
   * action buttons
   */
  MINIMIZED_HEADER = 'Minimized Header',
  NO_HEADER = 'No Header',
  /**
   * Reduced Header renders the following:
   *
   * - Global Navigation
   * - Site logo and title
   * - CTAs and Site Search
   *
   * It does NOT render the following:
   *
   * - Local navigation items.
   */
  REDUCED_HEADER = 'Reduced Header',
  /**
   * Simple header hides all the navigation items, keeping just the snap logo and name of the
   * website
   */
  SIMPLE_HEADER = 'Simple Header',
}

/**
 * Simple footer allows to show a narrow basic footer bar with only essential links.
 *
 * NOTE: These values must match validation in contentful.
 */
export enum FooterView {
  FULL_FOOTER = 'Full Footer',
  NO_FOOTER = 'No Footer',
  SIMPLE_FOOTER = 'Simple Footer',
}

export interface PageLayoutContextProps {
  footerView: FooterView;
  headerView: HeaderView;
  headerBackgroundColorOverride?: BackgroundColor;
  hasSideNav: boolean;
  hasSubNav: boolean;
  /* This covers the case where a page uses a custom side nav that is not created in Contentful
   * using the 'SideNavigation' content type and rendered at the slug level.
   *
   * An example of this is the snap.com home page where the side nav is part of a custom block.
   *
   * We cannot use the 'hasSideNav' property as that will also lead to other changes that are not
   * appropriate for this use case, such as a different max-width for the page container.
   */
  hasCustomSideNav: boolean;
  setFooterView?: (footerView: FooterView) => void;
  setHeaderView?: (headerView: HeaderView) => void;
  setHeaderBackgroundColorOverride?: (color?: BackgroundColor) => void;
  setHasSideNav?: (hasSideNav: boolean) => void;
  setHasSubNav?: (hasSubNav: boolean) => void;
  setHasCustomSideNav?: (hasCustomSideNav: boolean) => void;
}

export const PageLayoutContext = createContext<PageLayoutContextProps>({
  footerView: FooterView.FULL_FOOTER,
  headerView: HeaderView.FULL_HEADER,
  hasSideNav: false,
  hasSubNav: false,
  hasCustomSideNav: false,
});
